import React, { Fragment, useCallback, useState } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import moment from "moment-timezone";
import Select from "react-select";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Collapse from "@mui/material/Collapse";
import Box from "@mui/material/Box";

const targetingPaymentsOptions = [
  { label: "None", value: "" },
  { label: "Clients", value: "clients" },
  { label: "Operators", value: "operators" },
  { label: "AMEX", value: "amex" },
];

const XeroAccountRow = ({ account, disabled, handleModifyAccount }) => {
  const handleChangeTargetingPayments = useCallback((option) => {
    handleModifyAccount(account._id, { targetingPayments: option?.value || null });
  }, [account, handleModifyAccount]);

  const handleDisable = useCallback((event) => {
    event.stopPropagation();

    const { checked } = event.target;

    handleModifyAccount(account._id, { disabled: !checked });
  }, [account, handleModifyAccount]);

  if (!account) {
    return null;
  }

  return (
    <Fragment>
      <TableRow
        sx={{
          "& > *": { borderBottom: "unset" },
          backgroundColor: disabled || account.disabled ? "#ffebee !important" : undefined,
        }}
        className="fm-l2-parent-table-row"
      >
        <TableCell>{account.bankAccountNumber}</TableCell>
        <TableCell>{account.name}</TableCell>
        <TableCell>-</TableCell>
        <TableCell>
          <Select
            options={targetingPaymentsOptions}
            placeholder="Select"
            name={`targetingPayments[${account.id}]`}
            value={targetingPaymentsOptions.find((option) => option.value === account.targetingPayments) || targetingPaymentsOptions[0]}
            onChange={handleChangeTargetingPayments}
            menuPosition="fixed"
            menuPortalTarget={document.body}
            closeMenuOnSelect
            disabled={disabled}
          />
        </TableCell>
        <TableCell>
          <Checkbox
            checked={!disabled && !account.disabled}
            onChange={handleDisable}
            name={`disabled[${account.id}]`}
            disabled={disabled}
          />
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

const XeroTenantRow = ({ tenant, handleModifyTenant, handleModifyAccount }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOpen = useCallback((event) => {
    event.stopPropagation();

    if (event.target.type === "checkbox") {
      return;
    }

    setIsOpen(open => !open);
  }, []);

  const handleDisable = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();

    const { checked } = event.target;

    handleModifyTenant(tenant._id, { disabled: !checked });
  }, [tenant, handleModifyTenant]);

  if (!tenant) {
    return null;
  }

  return (
    <Fragment>
      <TableRow
        sx={{
          "& > *": { borderBottom: "unset" },
          backgroundColor: tenant.disabled ? "#ffebee !important" : undefined,
        }}
        onClick={toggleOpen}
        className="fm-l2-parent-table-row"
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={toggleOpen}
            className="p-0"
          >
            {isOpen ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
          </IconButton>
        </TableCell>
        <TableCell>
          {moment(tenant.createdAt).format("M/D/YY")}
        </TableCell>
        <TableCell>{tenant.tenantName}</TableCell>
        <TableCell>
          <Checkbox
            checked={!tenant.disabled}
            onChange={handleDisable}
            name={`disabled[${tenant._id}]`}
          />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Table aria-label="collapsible table">
                <TableHead className="fm-l2-child-table-head dynamic-accordion-table-header">
                  <TableRow className="fm-l2-child-table-head-row">
                    <TableCell>
                      Account Number
                    </TableCell>
                    <TableCell>
                      Name
                    </TableCell>
                    <TableCell>
                      Available Balance
                    </TableCell>
                    <TableCell>
                      Targeting Payments
                    </TableCell>
                    <TableCell>
                      Enabled
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody className="fm-l2-child-table-body">
                  {!tenant.accounts?.length && (
                    <TableRow
                      className="fm-l2-parent-table-row"
                    >
                      <TableCell colSpan={5}>No accounts yet</TableCell>
                    </TableRow>
                  )}
                  {tenant.accounts?.length > 0 && tenant.accounts.map((account) =>
                    <XeroAccountRow
                      key={account.id}
                      account={account}
                      disabled={tenant.disabled}
                      handleModifyAccount={handleModifyAccount}
                    />,
                  )}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

const XeroTokensTable = ({ data, handleModifyTenant, handleModifyAccount }) => {
  return (
    <Fragment>
      <TableContainer
        component={Paper}
        className="accordion-table-with-details shadow-none"
      >
        <Table aria-label="collapsible table">
          <TableHead className="fm-l2-parent-table-head dynamic-accordion-table-header">
            <TableRow className="fm-l2-parent-table-head-row">
              <TableCell/>
              <TableCell>
                Linked Date
              </TableCell>
              <TableCell>
                Organization Name
              </TableCell>
              <TableCell>
                Enabled
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody className="fm-l2-parent-table-body">
            {!data.length && (
              <TableRow
                className="fm-l2-parent-table-row"
              >
                <TableCell colSpan={4}>No tenants linked yet</TableCell>
              </TableRow>
            )}

            {data.length > 0 && data.map((token) => token.tenants?.map((tenant) => (
              <XeroTenantRow
                key={tenant.id}
                tenant={tenant}
                handleModifyTenant={handleModifyTenant}
                handleModifyAccount={handleModifyAccount}
              />
            )))}
          </TableBody>
        </Table>
      </TableContainer>
    </Fragment>
  );
};

export default XeroTokensTable;
